/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2020, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import WebPreview from '@ibm-watson/shared/modules/actions/components/web-preview/WebPreview';
import { buildIntegrationsUrlCpd } from '@ibm-watson/shared/modules/channels/util/urls';
import {
  CONNECT_TO_AGENT_BEHAVIOR_TYPES,
  TOOLING_TYPES
} from '@ibm-watson/shared/modules/channels/util/web-chat';
import {
  isCpd,
  endpoints,
  deployEnv
} from '@ibm-watson/shared/modules/ia/util/config';
import { ctaEvents } from '@ibm-watson/shared/modules/ia/util/trackers/segment';
import { DeployEnv } from '../../../../../ts';
import { links } from '../links';

import './SearchPreview.scss';

const strings = {
  PREVIEW: 'Preview',
  WEB_CHAT_NAMESPACE: 'Search preview', // This must be unique across Watson Assistant.
  SEARCH_SKILL_WARNING:
    'This Preview only displays Search results. To preview the full end-user experience, use the full Assistant preview.',
  SEARCH_SKILL_PLEASE_SELECT_FIELDS:
    'Please select a value for Title, Body, and URL to see the answers to your query',
  CONVERSATIONAL_SEARCH: 'conversational_search',
  CONVERSATIONAL_SEARCH_TEXT:
    'This is how the generated response using conversational search is displayed in your assistant.',
  CONVERSATIONAL_SEARCH_TITLE: 'Source document',
  CONVERSATIONAL_SEARCH_DESCRIPTION:
    'Generated responses are supported by a link to the source document with a highlight of the passage that was used here.',
  HISTORY_NOTES_TYPE: 'HISTORY',
  HISTORY_NOTES_AUTHOR: 'watson',
  HISTORY_NOTES_AUTHOR_ID: 'na',
  HISTORY_NOTES_USER_ID: 'anonymous',
  HISTORY_NOTES_THREAD_ID: 'main'
};

export class SearchPreview extends React.PureComponent {
  constructor(props) {
    super(props);
    let region = this.props.region;

    if (deployEnv === DeployEnv.DEV) {
      region = 'dev';
    }
    if (deployEnv === DeployEnv.STAGE) {
      region = 'staging';
    }
    this.state = {
      hasReceivedFirstMessage: false,
      customConfig: {
        region,
        namespace: strings.WEB_CHAT_NAMESPACE,
        ...(isCpd && { cloudPrivateHostURL: buildIntegrationsUrlCpd() })
      },
      customIBMConfig: {
        botNameOverride: strings.PREVIEW,
        connectToAgentBehavior: CONNECT_TO_AGENT_BEHAVIOR_TYPES.PREVIEW,
        customMessageURL: `${endpoints.store}/search/preview`,
        customInitConfigURL: `${endpoints.store}/skills/${this.props.skillId}/message/config`,
        customConfigURL: `${endpoints.store}/skills/${this.props.skillId}/message/config`,
        toolingType: TOOLING_TYPES.SEARCH
      }
    };
  }

  onPreSendMessage = event => {
    const { payload } = this.props;
    const payld = payload(event.data.input.text);
    _.set(event.data, 'input.options.alternate_responses', false);
    _.set(event.data, 'skillData', payld);
  };

  onSendMessage = () => {
    ctaEvents.searchWebchatMessage();
  };

  onPreReceive = event => {
    const { generic = [] } = event.data.output;
    const { hasReceivedFirstMessage } = this.state;
    const { hasAnyFieldsSelected, isConversationalSearch } = this.props;

    // For the first message, if conversational search is turned on,
    // we want to overwrite all messages received and return a
    // pre-defined and constant conversational message.
    // If conversational search is off, we want to add a constant
    // message to the front of the message stack.
    if (!hasReceivedFirstMessage) {
      if (isConversationalSearch) {
        // Remove everything from the message stack.
        while (generic.length > 0) {
          generic.pop();
        }
        generic.push({
          response_type: strings.CONVERSATIONAL_SEARCH,
          text: strings.CONVERSATIONAL_SEARCH_TEXT,
          citations: [
            {
              title: strings.CONVERSATIONAL_SEARCH_TITLE,
              url: links.CONVERSATIONAL_SEARCH_CITATION_URL,
              description: strings.CONVERSATIONAL_SEARCH_DESCRIPTION
            }
          ]
        });
      } else {
        generic.splice(0, 0, {
          text: strings.SEARCH_SKILL_WARNING,
          response_type: 'text'
        });
      }
      this.setState({ hasReceivedFirstMessage: true });
    }
    // When hasAnyFieldsSelected is false, should say a message saying please select title, body and url fields to get it displayed here.
    if (!isConversationalSearch && !hasAnyFieldsSelected) {
      generic.splice(1, 1, {
        text: strings.SEARCH_SKILL_PLEASE_SELECT_FIELDS,
        response_type: 'text'
      });
    }

    // For conversational search, we want to remove the message
    // received from store and instead push a fake conversational
    // search message.
    if (isConversationalSearch) {
      generic.pop();
      generic.push({
        response_type: strings.CONVERSATIONAL_SEARCH,
        text: strings.CONVERSATIONAL_SEARCH_TEXT,
        citations: [
          {
            title: strings.CONVERSATIONAL_SEARCH_TITLE,
            url: links.CONVERSATIONAL_SEARCH_CITATION_URL,
            description: strings.CONVERSATIONAL_SEARCH_DESCRIPTION
          }
        ]
      });
    }
  };

  onPreReset = () => {
    this.setState({
      hasReceivedFirstMessage: false
    });
  };

  render() {
    const { customConfig, customIBMConfig } = this.state;

    const { initiateWebchat, isConversationalSearch } = this.props;

    return (
      <WebPreview
        className="SearchPreview"
        customConfig={customConfig}
        customIBMConfig={customIBMConfig}
        onPreSend={this.onPreSendMessage}
        onPreReceive={this.onPreReceive}
        onSend={this.onSendMessage}
        onPreReset={this.onPreReset}
        initiateWebchat={initiateWebchat}
        isConversationalSearch={isConversationalSearch}
        open
        initialLoadingIndicator
      />
    );
  }
}

SearchPreview.defaultProps = {
  skillId: 'searchSkillIdPlaceHolder'
};

SearchPreview.propTypes = {
  region: PropTypes.string,
  skillId: PropTypes.string,
  payload: PropTypes.func,
  hasAnyFieldsSelected: PropTypes.bool,
  initiateWebchat: PropTypes.bool,
  isConversationalSearch: PropTypes.bool
};

const mapStateToProps = state => ({
  region: state.session.region
});

export default connect(mapStateToProps)(SearchPreview);
